import * as Sentry from "@sentry/react";
if (window.appConfig.react_sentry_dsn) {
    // init as early as possible
    Sentry.init({
        dsn: window.appConfig.react_sentry_dsn,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

import JSON5 from 'json5';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { useTranslation } from 'react-i18next';

import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';

import { authPath, authRoutes, AuthLayout } from './Auth.jsx';

import { contentPaneRoutes, route } from './app/routes';
import { ViewRedirectionGuard } from './app/ViewRedirectionGuard.jsx';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

// datepickers global localization
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// App objects
import theme from './app/theme.js';
import i18n, { detectLanguage } from '/@/i18n';
import { useApi} from '/@/app/Api';
import { AuthProvider } from '/@/Auth';
import { AppLayout } from './app/AppLayout.jsx';
import { AppContext } from '/@/app/AppContext';
import Questionnaire from '/@/app/components/questionnaire/Questionnaire.jsx';
import { SectionListState } from '/@/app/views/LabTestView/SectionListState.js';
import { messageStackState } from "/@/app/states/messageStackState";
import { userFlowState, injectApi } from "/@/app/states/userFlowState";
import { userState } from "/@/app/states/userState";
import { recordsInputReducer} from '/@/app/views/RecordsInputView';
import { TestLayout } from "/@/app/TestPage";

import './styles/base.sass';
import './styles/app.sass';

const params = new URLSearchParams(window.location.search);
if (params.has("start"))
{
	window.jwt = null;
	window.app_instance = null;
	localStorage.removeItem("jwt");
	localStorage.removeItem("jwt_refresh");
	localStorage.removeItem("app_instance");

	params.delete("start");
	const newSearch = params.toString();
	const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
	window.history.replaceState({}, "", newUrl);
};

//
// This holds context and renders only once.
// It should be a component, so it's able to create states with useState.
//
export const App = ({ Layout = AppLayout }) => {
    const api=useApi();

    //
    // Bootstrapped data from the server-side page
    //
    // Messages showed once on page load
    const [bootstrapData, setBootstrapData] = React.useState(null);
    const [djangoMessages, setDjangoMessages] = React.useState(null);
    const storesRef = React.useRef(null);
    const [ recordsInputState, recordsInputDispatch, ] = React.useReducer( recordsInputReducer, null);


    React.useEffect(() => {
        if (!bootstrapData) {
		api.request('cabinet/bootstrap/')
            .then((data) =>
	    {
		    try {
			    const refresh_token = localStorage.getItem('jwt_refresh');
			    let [, payload] = refresh_token.split('.');
			    payload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
			    if (payload.switched_from)
			    {
				    data.user.debug_mode=true;
				    data.user.switched_from=payload.switched_from;
			    };
		    } catch (err) {
			    console.error('Failed to decode JWT', err);
		    };
		    setBootstrapData(data);
		    i18n.changeLanguage(detectLanguage(data.settings));
	    })
            .catch(err => console.error("Failed to fetch bootstrapData:", err));
        };
    }, []);

    React.useEffect(() => { injectApi(api); }, [api]);
	/*
    React.useEffect(() => {
        if (!djangoMessages) {
            api.request('cabinet/messages/',{},{auth_navigate:false})
            .then(data => setDjangoMessages(data))
            .catch(function (err) { console.error("Failed to fetch djangoMessages:", err); setDjangoMessages([]) });
        };
    }, []);
    */

React.useEffect(() =>
{
	if (!bootstrapData) return;
	userFlowState.init(
	{
		globalPanel: bootstrapData.panel,
		panelsCount: bootstrapData.panels_count,
		user: bootstrapData.user,
		flow: bootstrapData.flow,
	});
	userState.init(bootstrapData.user);
}, [bootstrapData]);

if (!storesRef.current && bootstrapData && bootstrapData.panel)
{
	//
	//
	// All the MobX states should be created here. Not inside components.
	//
	//
	// Questionnaire
	//
	const sections = new Questionnaire.SectionList(bootstrapData.panel.id, api);
	const questionnaireSession = new Questionnaire.Session(sections, bootstrapData.panel);
	//
	// Lab Tests fields state
	//
	const labTestsSectionListState = new SectionListState( bootstrapData.measurements_by_section, bootstrapData.panel.id, api);
	storesRef.current = { questionnaireSession, labTestsSectionListState };
};

const stores = storesRef.current;

    if (!bootstrapData || !stores) return <div>Loading ...</div>;
    const { questionnaireSession, labTestsSectionListState } = stores;

    //if (!bootstrapData || !questionnaireSession || !labTestsSectionListState) return <div>Loading ...</div>;
	/*
    if (!bootstrapData || djangoMessages===null) return <div>Loading ...</div>;

    for (let [index, m] of djangoMessages.entries()) {
        m.autoHide = 6000 + 6000*index;  // 6 seconds auto-hide, one by one
        messageStackState.addMessage(m);
    }
    */

    const userData = bootstrapData.user

    //
    // Setup global user state
    //
    const featureEnabled = (name) => (bootstrapData.features.indexOf(name) > -1);

    let mainViewportRef = null;  // used to control scrolling



    return (
	<>
            <AppContext.Provider value={
                {
                    userData,  // static
                    versionData: bootstrapData.version,  // static
                    bootstrapData,  // all the data
                    // hide/show logic:
                    featureEnabled: featureEnabled,
                    showDynamicsPage: (
                        featureEnabled('dynamics_widget')
                        && bootstrapData.panels_count > 1
                    ),
                    // MobX states:
                    questionnaireSession,
                    labTestsSectionListState,
                    // React states:
                    recordsInputState,
                    recordsInputDispatch,

                    mainViewportRef,
                }
            }>
	    <Layout/>
            </AppContext.Provider>
	</>
    )
}

const router = createBrowserRouter([
{
	element: <AuthProvider />,
	children:
	[
		{
			path: route("/"),
			element: <App />,
			children: [ { element: <ViewRedirectionGuard userFlowState={userFlowState} />, children: contentPaneRoutes } ],
		},
		{
			path: "/test",
			element: <App Layout={TestLayout} />,
		},
	],
},
{
	path: '/',
	element: <AuthLayout />,
	children: [ { index: true, element: <Navigate to="/cabinet" replace /> }, ...authRoutes ],
},
]);


// prevent double root warning for HMR reload in strict mode
const container = document.getElementById("spa-container");
if (!container._root) container._root = createRoot(container);

container._root.render(
<LocalizationProvider key={i18n.language} dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
<ThemeProvider theme={theme}>
<CssBaseline />
<RouterProvider router={router} />
</ThemeProvider>
</LocalizationProvider>
);
